// Pure telesales logic — no React/Firebase. Call warnings, DND, lost/dormant
// classification, and the option sets used by the call/feedback workflows.

export const CALL_TYPES = [
    'Feedback Call', 'Repeat Order Call', 'Complaint Follow-up', 'COD Confirmation',
    'Doctor Follow-up', 'Delivery Follow-up', 'Winback / Lost Customer',
];
export const CALL_RESULTS = [
    'Connected', 'Not Answered', 'Busy', 'Switched Off', 'Wrong Number', 'Call Later',
    'Interested', 'Not Interested', 'Ordered', 'Complaint Raised',
];
export const CUSTOMER_MOODS = ['Positive', 'Neutral', 'Negative'];
export const FEEDBACK_RESULTS = [
    'Good Result', 'No Result', 'Pimples', 'Dryness', 'Burning', 'Skin Irritation',
    'Dandruff', 'Stopped Using', 'Wants Doctor Call',
];
export const NEGATIVE_FEEDBACK = new Set(['No Result', 'Pimples', 'Dryness', 'Burning', 'Skin Irritation', 'Dandruff', 'Wants Doctor Call']);
export const TELECALLER_REMARK_TYPES = ['feedback', 'offer', 'complaint', 'repeat_order', 'lost_customer', 'general'];
export const WINBACK_ACTIONS = ['Call for reorder', 'Offer discount', 'Add follow-up', 'Mark not interested', 'Mark DND'];

const DAY = 86400000;
const toList = (v) => (v ? (Array.isArray(v) ? v : Object.values(v)) : []);
const toMs = (v) => { if (v == null) return 0; const n = typeof v === 'number' ? v : new Date(v).getTime(); return Number.isNaN(n) ? 0 : n; };

// Most-recent call from a customer's callHistory.
export const lastCall = (callHistory) => {
    const list = toList(callHistory);
    if (!list.length) return null;
    return list.slice().sort((a, b) => toMs(b.calledAt) - toMs(a.calledAt))[0];
};

// 15-day call warning. Returns { warn, daysSince, lastCall, message }.
export const callWarning = (callHistory, now = Date.now()) => {
    const lc = lastCall(callHistory);
    if (!lc) return { warn: false, lastCall: null };
    const daysSince = Math.floor((now - toMs(lc.calledAt)) / DAY);
    const warn = daysSince <= 15;
    const date = lc.calledAt ? new Date(toMs(lc.calledAt)).toLocaleDateString('en-IN') : '—';
    return {
        warn, daysSince, lastCall: lc,
        message: warn ? `This customer was already contacted on ${date} by ${lc.calledBy || 'an agent'}. Result: ${lc.result || '—'}. Remark: ${lc.remark || '—'}.` : '',
    };
};

export const isDND = (customer) => customer?.dnd === true || customer?.doNotDisturb === true;

// --- Do Not Ship / Spam flag ---
export const DO_NOT_SHIP_REASONS = [
    'Repeated COD Return',
    'Spam / Fake Order',
    'Fake / Invalid Address',
    'Abusive Customer',
    'Other',
];

// Returns { flagged, reason, note, by, at } for a customer record.
export const doNotShipStatus = (customer) => {
    const d = customer?.doNotShip;
    if (d && d.flagged) {
        return { flagged: true, reason: d.reason || 'Other', note: d.note || '', by: d.by || '', at: d.at || 0 };
    }
    return { flagged: false, reason: '', note: '', by: '', at: 0 };
};

// --- Refund Given flag ---
export const refundGivenStatus = (customer) => {
    const r = customer?.refundGiven;
    if (r && r.flagged) {
        return { flagged: true, amount: r.amount || 0, ref: r.reference || '', by: r.by || '', at: r.at || 0 };
    }
    return { flagged: false, amount: 0, ref: '', by: '', at: 0 };
};

export const daysSinceLastOrder = (customer, now = Date.now()) => {
    const last = toMs(customer?.lastOrderDate || customer?.lastPurchaseAt);
    return last ? Math.floor((now - last) / DAY) : null;
};

// Active / Dormant / Lost classification + composite badge.
export const lostStatus = (customer, now = Date.now()) => {
    const days = daysSinceLastOrder(customer, now);
    let status = 'Active';
    if (days != null) { if (days > 90) status = 'Lost'; else if (days > 60) status = 'Dormant'; }
    let badge = status;
    if (status === 'Lost') {
        if (customer?.customerType === 'VIP') badge = 'VIP Lost';
        else if (customer?.repeatCustomer === true) badge = 'Repeat Lost';
    }
    return { status, badge, daysSinceLastOrder: days };
};

// Last telecaller remark (most recent).
export const lastTelecallerRemark = (telecallerRemarks) => {
    const list = toList(telecallerRemarks);
    if (!list.length) return null;
    return list.slice().sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt))[0];
};

// Per-agent telecaller performance from a flat list of calls.
export const computeTelecallerStats = (calls = []) => {
    const by = {};
    for (const c of calls) {
        const a = c.calledBy || 'unknown';
        const s = by[a] || (by[a] = { agent: a, calls: 0, connected: 0, ordered: 0, followUps: 0, complaints: 0 });
        s.calls += 1;
        if (c.result === 'Connected' || c.result === 'Interested' || c.result === 'Ordered') s.connected += 1;
        if (c.result === 'Ordered') s.ordered += 1;
        if (c.followUpAt) s.followUps += 1;
        if (c.result === 'Complaint Raised') s.complaints += 1;
    }
    return Object.values(by);
};
